import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DateRangePickerComponent,
  OrganizationChatterAverageResponseTimeByTimeChart,
  OrganizationChatterManualVsAiByTimeChart,
  OrganizationChatterPpvRateByTimeChart,
  OrganizationChatterTotalChatsByChatterChart,
  OrganizationChatterTotalChatsByTimeChart,
  OrganizationChatterTotalEarningsByChatterChart,
  OrganizationChatterTotalEarningsByTimeChart,
  OrganizationChatterTotalMessagesByChatterChart,
  OrganizationChatterTotalMessagesByTimeChart,
  SelectAlt,
} from '../../../components';
import {
  fetchChatterAverageResponseTimeByTime,
  fetchChatterAverageResponseTimeByChatter,
  fetchChatterManualVsAiByTime,
  fetchChatterManualVsAiByChatter,
  fetchChatterPpvRateByTime,
  fetchChatterPpvRateByChatter,
  fetchChatterTableData,
  fetchChatterTotalChatsByChatter,
  fetchChatterTotalChatsByTime,
  fetchChatterTotalEarningsByChatter,
  fetchChatterTotalEarningsByTime,
  fetchChatterTotalMessagesByChatter,
  fetchChatterTotalMessagesByTime,
} from '../../../store/slices/organizationAnalytics/asyncThunks';
import {
  selectDateRange,
  selectFetchChatterAverageResponseTimeByTimeInfo,
  selectFetchChatterAverageResponseTimeByChatterInfo,
  selectFetchChatterManualVsAiByTimeInfo,
  selectFetchChatterManualVsAiByChatterInfo,
  selectFetchChatterPpvRateByTimeInfo,
  selectFetchChatterPpvRateByChatterInfo,
  selectFetchChatterTableDataInfo,
  selectFetchChatterTotalChatsByChatterInfo,
  selectFetchChatterTotalChatsByTimeInfo,
  selectFetchChatterTotalEarningsByChatterInfo,
  selectFetchChatterTotalEarningsByTimeInfo,
  selectFetchChatterTotalMessagesByChatterInfo,
  selectFetchChatterTotalMessagesByTimeInfo,
  selectSelectedChatterIds,
  selectSelectedChatterInfluencerId,
  setDateRange,
  setSelectedChatterIds,
  setSelectedChatterInfluencerId,
} from '../../../store/slices/organizationAnalytics/slice';
import {
  Content,
  FilterField,
  FiltersCard,
  FiltersGrid,
  FullWidthItem,
  ChartsGrid,
} from '../OrganizationAnalyticsPage/OrganizationAnalyticsPage.styles';
import { Text14Regular } from '../../../components/utils/Text/Text.styles';
import {
  fetchAllOrganizationChatters,
  fetchAllOrganizationInfluencers,
} from '../../../store/slices/chatter/asyncThunks';
import { selectChattersOptions, selectInfluencerOptions } from '../../../store/slices/chatter/slice';
import {
  OrganizationChatterAverageResponseTimeByChatterChart,
  OrganizationChatterManualVsAiByChatterChart,
  OrganizationChatterOffersByChatterChart,
  OrganizationChatterOffersByTimeChart,
  OrganizationChatterPpvRateByChatterChart,
  OrganizationChatterPurchasesByChatterChart,
  OrganizationChatterPurchasesByTimeChart,
  OrganizationAnalyticsChatterTable,
  OrganizationImpactPerChatterChart,
} from '../../../components/organizationAnalytics';
import useDebouncedValue from '../../../hooks/useDebouncedValue';

const OrganizationChatterAnalyticsPage = () => {
  const FILTER_DEBOUNCE_DELAY = 300;
  const dispatch = useDispatch();
  const dateRange = useSelector(selectDateRange);
  const influencerOptions = useSelector(selectInfluencerOptions);
  const chatterOptions = useSelector(selectChattersOptions);
  const selectedChatterInfluencerId = useSelector(selectSelectedChatterInfluencerId);
  const selectedChatterIds = useSelector(selectSelectedChatterIds);
  const totalEarningsByTimeInfo = useSelector(selectFetchChatterTotalEarningsByTimeInfo);
  const totalEarningsByChatterInfo = useSelector(selectFetchChatterTotalEarningsByChatterInfo);
  const totalMessagesByTimeInfo = useSelector(selectFetchChatterTotalMessagesByTimeInfo);
  const totalMessagesByChatterInfo = useSelector(selectFetchChatterTotalMessagesByChatterInfo);
  const totalChatsByTimeInfo = useSelector(selectFetchChatterTotalChatsByTimeInfo);
  const totalChatsByChatterInfo = useSelector(selectFetchChatterTotalChatsByChatterInfo);
  const averageResponseTimeByTimeInfo = useSelector(selectFetchChatterAverageResponseTimeByTimeInfo);
  const averageResponseTimeByChatterInfo = useSelector(selectFetchChatterAverageResponseTimeByChatterInfo);
  const manualVsAiByTimeInfo = useSelector(selectFetchChatterManualVsAiByTimeInfo);
  const manualVsAiByChatterInfo = useSelector(selectFetchChatterManualVsAiByChatterInfo);
  const ppvRateByTimeInfo = useSelector(selectFetchChatterPpvRateByTimeInfo);
  const ppvRateByChatterInfo = useSelector(selectFetchChatterPpvRateByChatterInfo);
  const chatterTableDataInfo = useSelector(selectFetchChatterTableDataInfo);

  const selectedInfluencer = useMemo(
    () => influencerOptions.find(({ value }) => value === selectedChatterInfluencerId) || null,
    [influencerOptions, selectedChatterInfluencerId],
  );
  const selectedChatters = useMemo(
    () => chatterOptions.filter(({ value }) => selectedChatterIds.includes(value)),
    [chatterOptions, selectedChatterIds],
  );
  const debouncedSelectedInfluencerId = useDebouncedValue(selectedChatterInfluencerId, FILTER_DEBOUNCE_DELAY);
  const debouncedSelectedChatterIds = useDebouncedValue(selectedChatterIds, FILTER_DEBOUNCE_DELAY);

  const chatterIds = useMemo(
    () => [...debouncedSelectedChatterIds].sort(),
    [debouncedSelectedChatterIds],
  );
  const influencerId = debouncedSelectedInfluencerId;

  const requestData = useMemo(
    () => ({
      from: dateRange[0],
      to: dateRange[1],
      ...(influencerId && { influencerId }),
      ...(chatterIds.length && { chatterIds }),
    }),
    [chatterIds, dateRange, influencerId],
  );

  const chatterLabelMap = useMemo(
    () => chatterOptions.reduce((acc, current) => ({ ...acc, [current.value]: current.label }), {}),
    [chatterOptions],
  );
  const loading =
    totalEarningsByTimeInfo.pending ||
    totalEarningsByChatterInfo.pending ||
    totalMessagesByTimeInfo.pending ||
    totalMessagesByChatterInfo.pending ||
    totalChatsByTimeInfo.pending ||
    totalChatsByChatterInfo.pending ||
    averageResponseTimeByTimeInfo.pending ||
    averageResponseTimeByChatterInfo.pending ||
    manualVsAiByTimeInfo.pending ||
    manualVsAiByChatterInfo.pending ||
    ppvRateByTimeInfo.pending ||
    ppvRateByChatterInfo.pending ||
    chatterTableDataInfo.pending;

  useEffect(() => {
    dispatch(fetchAllOrganizationInfluencers());
    dispatch(fetchAllOrganizationChatters());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchChatterTotalEarningsByTime(requestData));
    dispatch(fetchChatterTotalEarningsByChatter(requestData));
    dispatch(fetchChatterTotalMessagesByTime(requestData));
    dispatch(fetchChatterTotalMessagesByChatter(requestData));
    dispatch(fetchChatterTotalChatsByTime(requestData));
    dispatch(fetchChatterTotalChatsByChatter(requestData));
    dispatch(fetchChatterAverageResponseTimeByTime(requestData));
    dispatch(fetchChatterAverageResponseTimeByChatter(requestData));
    dispatch(fetchChatterManualVsAiByTime(requestData));
    dispatch(fetchChatterManualVsAiByChatter(requestData));
    dispatch(fetchChatterPpvRateByTime(requestData));
    dispatch(fetchChatterPpvRateByChatter(requestData));
    dispatch(fetchChatterTableData(requestData));
  }, [dispatch, requestData]);

  return (
    <Content>
      <FiltersCard>
        <FiltersGrid>
          <SelectAlt
            placeholder="All creators"
            label="Creator"
            options={influencerOptions}
            selectedOption={selectedInfluencer}
            handleChange={value => dispatch(setSelectedChatterInfluencerId(value ? value.value : null))}
            disabled={loading}
            isSearchable
            isClearable
          />
          <SelectAlt
            placeholder="All chatters"
            label="Chatters"
            options={chatterOptions}
            selectedOption={selectedChatters}
            handleChange={value => dispatch(setSelectedChatterIds((value || []).map(chatter => chatter.value)))}
            disabled={loading}
            multiSelection
            isSearchable
          />
          <FilterField>
            <Text14Regular>Date Range</Text14Regular>
            <DateRangePickerComponent
              selectDateRangeFn={selectDateRange}
              setDateRangeAction={setDateRange}
              width={240}
              disabled={loading}
            />
          </FilterField>
        </FiltersGrid>
      </FiltersCard>
      <ChartsGrid>
        <FullWidthItem>
          <OrganizationAnalyticsChatterTable
            data={chatterTableDataInfo.data}
            chatterLabelMap={chatterLabelMap}
            pending={chatterTableDataInfo.pending}
          />
        </FullWidthItem>
        <FullWidthItem>
          <OrganizationChatterTotalEarningsByTimeChart
            data={totalEarningsByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={totalEarningsByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterTotalEarningsByChatterChart
          data={totalEarningsByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={totalEarningsByChatterInfo.pending}
        />
        <OrganizationImpactPerChatterChart
          data={totalEarningsByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          selectedChatterIds={chatterIds}
          pending={totalEarningsByChatterInfo.pending}
        />
        <FullWidthItem>
          <OrganizationChatterTotalMessagesByTimeChart
            data={totalMessagesByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={totalMessagesByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterTotalMessagesByChatterChart
          data={totalMessagesByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={totalMessagesByChatterInfo.pending}
        />
        <FullWidthItem>
          <OrganizationChatterTotalChatsByTimeChart
            data={totalChatsByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={totalChatsByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterTotalChatsByChatterChart
          data={totalChatsByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={totalChatsByChatterInfo.pending}
        />
        <FullWidthItem>
          <OrganizationChatterAverageResponseTimeByTimeChart
            data={averageResponseTimeByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={averageResponseTimeByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterAverageResponseTimeByChatterChart
          data={averageResponseTimeByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={averageResponseTimeByChatterInfo.pending}
        />
        <FullWidthItem>
          <OrganizationChatterManualVsAiByTimeChart
            data={manualVsAiByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={manualVsAiByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterManualVsAiByChatterChart
          data={manualVsAiByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={manualVsAiByChatterInfo.pending}
        />
        <FullWidthItem>
          <OrganizationChatterOffersByTimeChart
            data={ppvRateByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={ppvRateByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterOffersByChatterChart
          data={ppvRateByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={ppvRateByChatterInfo.pending}
        />
        <FullWidthItem>
          <OrganizationChatterPurchasesByTimeChart
            data={ppvRateByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={ppvRateByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterPurchasesByChatterChart
          data={ppvRateByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={ppvRateByChatterInfo.pending}
        />
        <FullWidthItem>
          <OrganizationChatterPpvRateByTimeChart
            data={ppvRateByTimeInfo.data}
            chatterLabelMap={chatterLabelMap}
            dateRange={dateRange}
            pending={ppvRateByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationChatterPpvRateByChatterChart
          data={ppvRateByChatterInfo.data}
          chatterLabelMap={chatterLabelMap}
          pending={ppvRateByChatterInfo.pending}
        />
      </ChartsGrid>
    </Content>
  );
};

export default OrganizationChatterAnalyticsPage;
